/**
 * service-mail PO Box client. Mirrors:
 *   apps/service-mail/src/modules/pobox/pobox.controller.ts
 *
 * RBAC: read methods need Mail/readPoBox; mutations need Mail/managePoBox.
 */
import { env } from '../env';

import { request } from './client';

export type PoBoxTier = 'SMALL' | 'MEDIUM' | 'LARGE';
export type PoBoxStatus = 'AVAILABLE' | 'RENTED' | 'RESERVED' | 'RETIRED';

export interface PoBoxDto {
  readonly id: string;
  readonly boxNumber: string;
  readonly outletCode: string;
  readonly tier: PoBoxTier;
  readonly status: PoBoxStatus;
   
  readonly monthlyFeeMinor: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PoBoxRentalDto {
  readonly id: string;
  readonly poBoxId: string;
  readonly customerId: string;
  readonly customerName: string;
  readonly customerPhone: string;
  readonly startDate: string;
  readonly endDate: string | null;
   
  readonly monthlyFeeMinor: number;
  readonly status: string;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListPoBoxResponse {
  readonly items: readonly PoBoxDto[];
  // eslint-disable-next-line upmp/no-number-money -- row count
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListPoBoxQuery {
  readonly outletCode?: string;
  readonly status?: PoBoxStatus;
  readonly tier?: PoBoxTier;
  readonly page?: number;
  readonly pageSize?: number;
}

const baseUrl = (): string => env.serviceMailBaseUrl;

export async function listPoBoxes(query: ListPoBoxQuery = {}): Promise<ListPoBoxResponse> {
  return request<ListPoBoxResponse>(baseUrl(), '/v1/pobox', {
    method: 'GET',
    query: {
      outletCode: query.outletCode,
      status: query.status,
      tier: query.tier,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export interface CreatePoBoxBody {
  readonly boxNumber: string;
  readonly outletCode: string;
  readonly tier: PoBoxTier;
   
  readonly monthlyFeeMinor: number;
  readonly status?: PoBoxStatus;
}

export async function createPoBox(body: CreatePoBoxBody): Promise<PoBoxDto> {
  return request<PoBoxDto>(baseUrl(), '/v1/pobox', { method: 'POST', body });
}

export interface UpdatePoBoxBody {
  readonly boxNumber?: string;
  readonly outletCode?: string;
  readonly tier?: PoBoxTier;
  readonly status?: PoBoxStatus;
   
  readonly monthlyFeeMinor?: number;
}

export async function updatePoBox(id: string, body: UpdatePoBoxBody): Promise<PoBoxDto> {
  return request<PoBoxDto>(baseUrl(), `/v1/pobox/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export interface RentPoBoxBody {
  readonly customerId: string;
  readonly customerName: string;
  readonly customerPhone: string;
   
  readonly monthlyFeeMinor: number;
  readonly startDate: string;
  readonly endDate?: string;
  readonly notes?: string;
}

export interface RentPoBoxResponse {
  readonly poBox: PoBoxDto;
  readonly rental: PoBoxRentalDto;
}

export async function rentPoBox(id: string, body: RentPoBoxBody): Promise<RentPoBoxResponse> {
  return request<RentPoBoxResponse>(baseUrl(), `/v1/pobox/${encodeURIComponent(id)}/rent`, {
    method: 'POST',
    body,
  });
}

export async function cancelPoBoxRental(id: string): Promise<RentPoBoxResponse> {
  return request<RentPoBoxResponse>(
    baseUrl(),
    `/v1/pobox/${encodeURIComponent(id)}/cancel-rental`,
    { method: 'POST' },
  );
}

export async function listPoBoxRentals(id: string): Promise<readonly PoBoxRentalDto[]> {
  return request<readonly PoBoxRentalDto[]>(
    baseUrl(),
    `/v1/pobox/${encodeURIComponent(id)}/rentals`,
    { method: 'GET' },
  );
}
